const AMAZON_ASSOCIATE_TAG = "padpulse-20";

const AMAZON_DOMAINS = [
  "amazon.com",
  "www.amazon.com",
  "amzn.to",
  "amzn.com",
];

function isAmazonUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();
    return AMAZON_DOMAINS.some((d) => hostname === d || hostname.endsWith("." + d));
  } catch {
    return false;
  }
}

/**
 * Given a product URL, returns an Amazon affiliate URL by appending
 * `tag=padpulse-20` only when the URL is an Amazon domain (handles
 * existing query strings correctly). Returns null for non-Amazon URLs.
 */
export function buildAffiliateLink(productUrl: string | null | undefined): string | null {
  if (!productUrl) return null;
  if (!isAmazonUrl(productUrl)) return null;
  try {
    const parsed = new URL(productUrl);
    parsed.searchParams.set("tag", AMAZON_ASSOCIATE_TAG);
    return parsed.toString();
  } catch {
    return null;
  }
}

// An Amazon ASIN is a 10-character alphanumeric token (e.g. B07XYZ1234).
const ASIN_PATTERN = /(?:\/dp\/|\/gp\/product\/|\/gp\/aw\/d\/|\/product\/|\/)([A-Z0-9]{10})(?:[/?]|$)/i;

/**
 * Extracts the ASIN (Amazon Standard Identification Number) from an Amazon
 * product URL, looking at the common `/dp/<ASIN>`, `/gp/product/<ASIN>`, and
 * bare `/<ASIN>/` path shapes. Returns the upper-cased ASIN or null when none
 * is found. Non-Amazon / unparseable URLs return null.
 */
export function extractAmazonAsin(url: string | null | undefined): string | null {
  if (!url) return null;
  if (!isAmazonUrl(url)) return null;
  let pathname: string;
  try {
    pathname = new URL(url).pathname;
  } catch {
    return null;
  }
  const match = pathname.match(ASIN_PATTERN);
  return match ? match[1].toUpperCase() : null;
}

/**
 * Compacts a messy Amazon product URL into a clean, tracking-free canonical
 * form `https://www.amazon.com/dp/<ASIN>` when an ASIN can be extracted. This
 * strips referral params, session tokens, and SEO slugs that hosts often paste
 * in. Non-Amazon URLs and Amazon URLs without a recognizable ASIN (e.g. search
 * pages or `amzn.to` short links we can't expand) are returned unchanged so no
 * link is ever lost.
 */
export function compactAmazonUrl(url: string | null | undefined): string | null {
  if (!url) return url ?? null;
  const asin = extractAmazonAsin(url);
  if (!asin) return url;
  return `https://www.amazon.com/dp/${asin}`;
}
